import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import clsx from 'clsx';
import Collapse from '@mui/material/Collapse';
import { Box, Button, Modal, Typography } from '@mui/material';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { styled, alpha } from '@mui/material/styles';
import { useTreeItem2 } from '@mui/x-tree-view/useTreeItem2';
import { TreeItem2Checkbox, TreeItem2Content, TreeItem2IconContainer, TreeItem2Label } from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon';
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider';
import { TreeItem2DragAndDropOverlay } from '@mui/x-tree-view/TreeItem2DragAndDropOverlay';

import { PieChart } from '@mui/x-charts/PieChart';

import Api from "../Services/Api";
import { myLog, IconFromString } from "../Services/Functions";
import loadingGif from '../Assets/loading.gif';
import CurrentOrder from '../Components/CurrentOrder';
import PcqCompile from '../Components/Pcq/PcqCompile';
import PcqNotes from '../Components/Pcq/PcqNotes';

function Pcq() {
    const [currentOrder, setCurrentOrder] = useState(null);
    const [treeViewData, setTreeViewData] = useState(null);
    //const [rtwPublicApi, setRtwPublicApi] = useState(null);
    const [crudOpen, setCrudOpen] = useState(false);
    const [notesPopup, setNotesPopup] = useState(null);
    const [crudSelectedId, setCrudSelectedId] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [searchParams] = useSearchParams();
    const expandedNodes = searchParams.get('expandedNodes');
    const [expanded, setExpanded] = useState(expandedNodes === null ? [] : (expandedNodes === "" ? [] : expandedNodes.split(','))); //Used for print page with same expandedItems
      
    /***** Tree View service functions *****/
    const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({        //Set style for elements in various conditions (hover, selected, etc..)
        flexDirection: 'row-reverse',
        borderRadius: theme.spacing(0.7),
        marginBottom: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
        padding: theme.spacing(0.5),
        paddingRight: theme.spacing(1),
        [`&.Mui-expanded `]: {
            '&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon': {
                color: theme.palette.primary.dark,
                ...theme.applyStyles('light', {
                    color: theme.palette.primary.main,
                }),
            },
            '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                left: '16px',
                top: '44px',
                height: 'calc(100% - 48px)',
                width: '1.5px',
                backgroundColor: theme.palette.grey[700],
                ...theme.applyStyles('light', {
                    backgroundColor: theme.palette.grey[300],
                }),
            },
        },
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: 'white',
            ...theme.applyStyles('light', {
                color: theme.palette.primary.main,
            }),
        },
        [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            ...theme.applyStyles('light', {
                backgroundColor: theme.palette.primary.main,
            }),
        },
    }));

    function TransitionComponent(props) {       //Transition function (soft expand)
        return <Collapse in={props.in} {...props} />;
    }

    const StyledTreeItemLabelText = styled(Typography)({    //Set style for label (can be removed or enriched)
        color: 'inherit',
    });

    function CustomLabel({ item, icon: Icon, crudLevel, children, lastOperation, color, ...other }) {
        return (
            <TreeItem2Label
                {...other}
                sx={{
                    display: 'flex',
                    alignItems: 'center', // Assicura che gli elementi siano allineati al centro verticalmente
                    justifyContent: 'space-between', // Allinea children a sinistra e lastOperation a destra
                    width: '100%', // Assicura che prenda tutta la larghezza del container
                    paddingTop: isMobile ? '20px' : 'inherit',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {Icon && (
                        <Box
                            component={Icon}
                            className="labelIcon"
                            sx={{ mr: 1, fontSize: '1.2rem', color: `${color} !important` }}
                        />
                    )}
                    <StyledTreeItemLabelText
                        style={{ 'fontSize': isMobile ? '1.3em' : 'inherit' }}
                        variant="body2"
                    >
                        {children}
                    </StyledTreeItemLabelText>

                    {isCrudLevel(item) && (
                        <Box
                            component={IconFromString("Edit")}
                            className="labelIcon"
                            sx={{ ml: 5, fontSize: '1.2rem', color: `${color} !important` }}
                            onClick={() => handleEditClick(item)}
                        />
                    )}

                    {item.notes && (
                        <Box
                            component={IconFromString("Message")}
                            className="labelIcon"
                            sx={{ ml: 1, fontSize: '1.2rem', color: `${color} !important` }}
                            onClick={() => handleNoteClick(item.notes)}
                        />                    
                    )}                    
                </Box>

                {lastOperation && (
                    <StyledTreeItemLabelText
                        style={{ 'fontSize': isMobile ? '1.3em' : 'inherit' }}
                        variant="body2"
                        sx={{
                            marginLeft: 'auto',
                        }}
                    >
                        {lastOperation}
                    </StyledTreeItemLabelText>
                )}
            </TreeItem2Label>
        );
    }

    const isCrudLevel = (item) => {
        if (item.crudLevel) {
            return true
        }
        else {
            return false;
        }
    };

    var statusIconMapping = {
        '-1' : { //History
            'icon': 'History',
        },        
        '1' : { //Da eseguire
            'icon': 'PanTool',
        },
        '2' : { //In corso
            'icon': 'Engineering',
        },
        '3' : { //Verificato e chiuso
            'icon': 'VerifiedUser',
        },
    };

    const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {   //Main customization function
        const { id, itemId, label, disabled, children } = props;
        const {
            getContentProps,
            getIconContainerProps,
            getCheckboxProps,
            getLabelProps,
            getGroupTransitionProps,
            getDragAndDropOverlayProps,
            status,
            publicAPI,
        } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });        //Headless API

        //setRtwPublicApi(publicAPI);
        const item = publicAPI.getItem(itemId);       //Retrieve source element (with all it's attributes)

        const crudLevel = isCrudLevel(item);
        let icon;
        let color;
        let lastOperation = item.lastOperation;

        if (!item.status) {
            icon = IconFromString("FolderRounded");
            color = 'inherit';
        }
        else {
            let factor = 1;
            if (item.status < 0) {
                factor = -1;
            }
            icon = IconFromString(statusIconMapping[item.status * factor].icon);
            color = item.color;
        }

        return (
            <TreeItem2Provider itemId={itemId}>
                <CustomTreeItemContent
                    {...getContentProps({
                        className: clsx('content', {
                            'Mui-expanded': status.expanded,
                            'Mui-selected': status.selected,
                            'Mui-focused': status.focused,
                            'Mui-disabled': status.disabled,
                        }),
                    })}
                >
                    <TreeItem2IconContainer {...getIconContainerProps()}>
                        <TreeItem2Icon status={status} />
                    </TreeItem2IconContainer>
                    <TreeItem2Checkbox {...getCheckboxProps()} />
                    <CustomLabel
                        {...getLabelProps({ item, icon, color, crudLevel: crudLevel, lastOperation })}
                    />
                    <TreeItem2DragAndDropOverlay {...getDragAndDropOverlayProps()} />
                </CustomTreeItemContent>
                {children && <TransitionComponent {...getGroupTransitionProps()} />}               
            </TreeItem2Provider>
        );
    });

    /***** Graph service functions *****/
    function calculateDiagramData() {
        //Calculated based on childrens of elements with "children" property
        const statusCounts = {};
        const statusDescriptions = [];
        let totalCount = 0;

        function addStatusDescription(status, description, color) {
            const exists = statusDescriptions.some(item => item.status === status);
            if (!exists) {
                statusDescriptions.push({ status, description, color });
            }
        }

        function processChildren(children) {
            children.forEach(child => {
                if (isCrudLevel(child)) {
                    const status = child.status;
                    const description = child.description;
                    const color = child.color;

                    addStatusDescription(status, description, color);

                    if (statusCounts[status]) {
                        statusCounts[status]++;
                    } else {
                        statusCounts[status] = 1;
                    }
                    totalCount++;
                }
            });
        }

        if (treeViewData) {
            treeViewData.forEach(item => {
                if (item.children) {
                    processChildren(item.children);
                }
            });
        }

        const statusPercentages = Object.keys(statusCounts).map((status, index) => {
            const value = (statusCounts[status] / totalCount) * 100;
            const statusData = statusDescriptions.find(item => item.status === parseInt(status));
            const description = statusData.description;
            const color = statusData.color;

            return {
                id: index,
                value: value.toFixed(2),
                label: `${description} (${value.toFixed(2)}%)`, // Usa la descrizione trovata
                color: color
            };
        });

        return [{
            data: statusPercentages,
        }];
    }

    /***** Main page functions *****/
    const handleItemSelectionToggle = (event, itemId, isSelected) => {
        /*
            if (isSelected) { 
                const item = rtwPublicApi.getItem(itemId);            
            }
        */
    };
   
    const handleEditClick = (item) => {   
        if (isCrudLevel(item)) {    //Already cheched - only for extra security
            setCrudSelectedId(item.id);    
            setCrudOpen(true);
        }
    };    

    const handleNoteClick = (notes) => {   
        setNotesPopup(notes);
    };    
        
    const handleExpandedItemsChange = (event, nodeIds) => {        
        setExpanded(nodeIds);
    };

    const handleClose = () => {
        setCrudOpen(false);
        setNotesPopup(null);
    };

    function updateCurrentOrder(newVal) {
        setCurrentOrder(newVal);
    }

    function handlePrint() {
        const expandedNodes = expanded.join(',');
        const popupWindow = window.open(window.location.href + `?print&expandedNodes=${expandedNodes}`, '_blank', 'width=800,height=600');

        if (popupWindow) {
            popupWindow.onload = () => {
                const styleElement = popupWindow.document.createElement('style');
                styleElement.innerHTML = `
                    /* Stili di stampa personalizzati */
                    @media print {
                        .noPrint {
                            display: none !important;
                        }
                    }
                `;
                popupWindow.document.head.appendChild(styleElement);

                setTimeout(() => {
                    popupWindow.document.close();
                    popupWindow.focus();
                    popupWindow.print();
                    //popupWindow.onafterprint = () => {
                        popupWindow.close();
                    //};
                }, 2000);
            };
        }
    }
    
    useEffect(() => {
		try {
            if (currentOrder) {
                Api.get({endpoint: `pcqs/list/${currentOrder.id}`, contentType : 'application/json'})
                    .then(response => {
                        setTreeViewData(response.data.pcqs);
                    })
                    .catch(error => {
                        myLog(JSON.stringify(error));
                    });
            }
		} catch (error) {
			myLog(JSON.stringify(error));
		}
    }, [currentOrder, lastUpdate]); 	// eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Modal
                open={crudOpen}
                onClose={handleClose}
                aria-labelledby="parent-modal-title"
                aria-describedby="parent-modal-description"
            >
                <PcqCompile selectedId={crudSelectedId} handleClose={handleClose} statusIconMapping={statusIconMapping} setLastUpdate={setLastUpdate} />
            </Modal>

            <Modal
                open={notesPopup}
                onClose={handleClose}
                aria-labelledby="parent-modal-title"
                aria-describedby="parent-modal-description"
            >
                <PcqNotes notes={notesPopup} handleClose={handleClose} />
            </Modal>
            
            <div>
                <CurrentOrder updateCurrentOrder={updateCurrentOrder} />

                {treeViewData ? (
                    <>
                        <RichTreeView
                            expandedItems={expanded}
                            onExpandedItemsChange={handleExpandedItemsChange}
                            items={treeViewData}
                            slots={{ item: CustomTreeItem }}
                            sx={{ height: 'fit-content', flexGrow: 1, overflowY: 'auto' }}
                            onItemSelectionToggle={handleItemSelectionToggle}
                        />
                        <hr />
                        <h3>Analisi globale PCQ</h3>
                        <PieChart
                            series={calculateDiagramData()}
                            width={900}
                            height={300}
                        />
                        <hr />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button className="noPrint" variant="contained" onClick={handlePrint}>Stampa pagina</Button>
                        </Box>                    
                    </>
                ) : (
                    <img className="pl-3" src={loadingGif} alt="Loading..."/>
                )}
            </div>
        </>
    );
}

export default Pcq;